import { Chip, Typography } from "@mui/material";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useActionRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { DataSubjectRequestDetailDto } from "../../../common/service/server/dto/DataSubjectRequestDto";
import { Iso8601Date } from "../../../common/service/server/Types";
import { Error as RequestsError } from "../../copy/requests";
import { Fmt } from "../../formatters";
import { DATE_FORMAT_MEDIUM } from "../../formatters/date";
import { fmtStateShort } from "../../formatters/request";
import { ExtendDataRequestDialog } from "../dialog/ExtendDataRequestDialog";
import { Routes } from "../../root/routes";
import { LoadingButton } from "../../../common/components/LoadingButton";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { AlertCard, Modal, ToolTip, useSnackBar } from "@truevault/aquilo";
import { Loading } from "../../../common/components/Loading";
import { Button as AquiloButton } from "@truevault/aquilo";
import "../../../../sass/components/data-request-subject-card.scss";

type DataRequestSubjectCardProps = {
  request: DataSubjectRequestDetailDto;
  primaryOrgId: string;
  org: OrganizationDto;
  refreshRequest: () => void;
  className?: string;
};

export const fmtDateForRow = (date?: Iso8601Date, relative = false) => {
  return relative ? Fmt.Date.fmtDateFromNow(date) : Fmt.Date.fmtDate(date, DATE_FORMAT_MEDIUM);
};

export const daysRemaining = (deadlineMoment) => daysBetween(moment(), deadlineMoment);

const canExtend = (request: DataSubjectRequestDetailDto): boolean => {
  const {
    requestDate,
    dueDate: dueDateStr,
    emailVerifiedDate,
    state,
    dataRequestType: { maxDaysOpen, extendDueDateDays, isDueFromVerificationDate },
    batchPublicId,
  } = request;

  if (batchPublicId) {
    return false; // Cannot extend from the batch workflow (the due days are all different)
  }

  const dueDate = moment(dueDateStr);
  let maxExtensionDate = moment(requestDate).add(maxDaysOpen, "days");
  if (isDueFromVerificationDate && emailVerifiedDate) {
    maxExtensionDate = moment(emailVerifiedDate).add(maxDaysOpen, "days");
  }
  const updatedDueDate = moment(dueDate).add(extendDueDateDays, "days");

  if (
    updatedDueDate.isAfter(maxExtensionDate, "days") ||
    updatedDueDate.isSame(dueDate, "days") ||
    state === "CLOSED"
  ) {
    return false;
  }
  return true;
};

/**
 * Some request types, like Limit requests, have had their Step 1 removed (verify consumer), where a user would normally
 * choose to process as a service provider. For these requests, we move the option to the dropdown menu since it's a low
 * volume workflow, and we save the clicks of the first step in the 95% case.
 *
 * @param organization The organization the request is for, used to determine if the org is a service provider.
 * @param request The data subject request to check if it can be processed as a service provider.
 * @return {boolean} True if the request can be processed as a service provider, false otherwise.
 */
const canProcessAsServiceProvider = (
  organization: OrganizationDto,
  request: DataSubjectRequestDetailDto,
): boolean => {
  const { state, batchPublicId } = request;

  if (batchPublicId) {
    return false; // Cannot process as service provider from the batch workflow
  }

  if (!organization.isServiceProvider) {
    // Org is not a service provider, so it cannot process requests as a service provider.
    return false;
  }

  if (state !== "LIMIT_DATA") {
    // Currently this is only for Limit requests, since we removed the Verify Consumer step. Other types still have this
    // option in the first step, so it isn't necessary in the dropdown.
    return false;
  }

  if (request.consumerGroupResult === "PROCESSED_AS_SERVICE_PROVIDER") {
    return false; // Already marked for processing as service provider.
  }

  return true;
};

const daysBetween = (startMoment, endMoment) => Math.round(endMoment.diff(startMoment, "days"));

export const SUBMIT_FIELD_NAMES = [
  "requestDate",
  "subjectEmailAddress",
  "subjectMailingAddress",
  "subjectPhoneNumber",
  "subjectFirstName",
  "subjectLastName",
  "dataRequestTypeId",
  "submissionMethod",
  "notice",
];

export const SubjectCard = ({
  request,
  primaryOrgId,
  org,
  refreshRequest,
  className = "",
}: DataRequestSubjectCardProps) => {
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [submitValues, setSubmitValues] = useState<any>(null);
  const [submitExtendValues, setSubmitExtendValues] = useState(null);
  const [isAttemptingAutomation, setIsAttemptingAutomation] = useState(false);
  const [showAutomationConfirm, setShowAutomationConfirm] = useState(false);
  const [automationSubmissionMessage, setAutomationSubmissionMessage] = useState<string | null>(
    null,
  );
  const [automationSubmissionErrors, setAutomationSubmissionErrors] = useState<string[] | null>(
    null,
  );

  const history = useHistory();
  const snackbar = useSnackBar();

  const isBatch = !!request.batchPublicId && !!request.batch;

  const handleExtendSubmit = (values) => {
    setSubmitExtendValues(Object.assign({}, values));
  };

  const { fetch: triggerUpdate } = useActionRequest({
    api: () => {
      const existingValues = Object.fromEntries(
        Object.entries(request).filter(([k]) => SUBMIT_FIELD_NAMES.includes(k)),
      );
      const processedSubmitValues = Object.fromEntries(
        Object.entries(submitValues)
          .filter(([k]) => SUBMIT_FIELD_NAMES.includes(k))
          .map(([k, v]) => {
            return k === "requestDate" && typeof (v as any).toISOString == "function"
              ? [k, (v as any).toISOString()]
              : [k, v];
          }),
      );
      const values = Object.assign({}, existingValues, processedSubmitValues) as any;
      return Api.dataRequest.updateRequest(primaryOrgId, request.id, values);
    },
    messages: { defaultError: RequestsError.UpdateRequest },
  });

  const submitRequestForAutomation = useCallback(async (orgId, requestId) => {
    return Api.dataRequest.manuallyAutomateRequest(orgId, requestId);
  }, []);

  const handleUserSubmitRequestForAutomation = async () => {
    setIsAttemptingAutomation(true);

    try {
      const result = await submitRequestForAutomation(primaryOrgId, request.id);
      if (!result.success) {
        setAutomationSubmissionMessage(result.message);
        setAutomationSubmissionErrors(result.errors);
      } else {
        history.push(Routes.requests.pending);
        snackbar(`Request ${request.id} is now pending automated processing.`, {
          variant: "success",
        });
      }
    } catch (err) {
      console.error("Error submitting request for automation", err);
      setAutomationSubmissionMessage("An error occurred while attempting to automate the request.");
    }
  };

  const handleManuallyAutomateClick = async () => {
    if (isBatch) {
      setShowAutomationConfirm(true);
    } else {
      await handleUserSubmitRequestForAutomation();
    }
  };

  const handleAutomationConfirm = async () => {
    setShowAutomationConfirm(false);
    await handleUserSubmitRequestForAutomation();
  };

  const { request: extendRequestState, fetch: triggerExtend } = useActionRequest({
    api: async (values: any) => {
      return Api.dataRequest.extendRequest(primaryOrgId, request.id, values);
    },
    onSuccess: () => window.location.reload(),
  });

  // make the request whenever submit values are set
  useEffect(() => {
    if (submitValues) {
      triggerUpdate();
      setSubmitValues(null);
    }
  }, [triggerUpdate, submitValues]);

  useEffect(() => {
    if (submitExtendValues) {
      triggerExtend(submitExtendValues);
      setSubmitExtendValues(null);
    }
  }, [triggerExtend, submitExtendValues]);

  // Only allow extension menu when NOT an Opt-Out and hasn't been extended.

  const showDueDate = request.state !== "CLOSED";

  // We auto-redirect to the inbox when automation is successful, so this block is just for showing the loading progress
  // and any errors that occurred during automation. If a batch fails, there is a useful list with each request that was
  // removed from the batch and the reason why.
  const automationErrorBlock = (
    <>
      <span>This may take a few moments. You'll be redirected to the inbox shortly.</span>
      {!(automationSubmissionMessage || automationSubmissionErrors?.length > 0) && (
        <>
          <Loading />
        </>
      )}
      {(automationSubmissionMessage || automationSubmissionErrors?.length > 0) && (
        <div className="mt-md">
          <AlertCard
            variant="warning"
            title={automationSubmissionMessage ?? "Errors occurred when submitting for automation:"}
            containerClasses="mb-0"
          >
            {automationSubmissionErrors?.length > 0 && (
              <ul className="mb-0 pl-md list-disc">
                {automationSubmissionErrors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            )}
          </AlertCard>
        </div>
      )}
    </>
  );

  return (
    <>
      <div className={`request-details-info-card tw-boundary ${className}`}>
        <div className={"text-t5 text-weight-medium header-row"}>
          <div className="header-left">
            <span className="request-type-title">
              Type: {request.dataRequestType?.label || "Unknown Request Type"}
            </span>

            {request.isDuplicate ? (
              <Chip
                clickable
                onClick={() => history.push(`/requests/${request.duplicatesRequestId}`)}
                className="ml-sm"
                label="DUPLICATE"
                color="warning"
              />
            ) : null}
            {request.context == "EMPLOYEE" ? (
              <Chip label="HR" color="secondary" className="ml-sm" />
            ) : undefined}
            {!isBatch && (
              <span className="ml-sm type-text">
                <span className="type-badge">{fmtStateShort(request.dataRequestType)}</span>
              </span>
            )}
          </div>
        </div>
        <div className="text-t2 info-row">
          {isBatch ? (
            <Chip
              label={`${request.batch.requestCount} requests - batch ${request.batchPublicId}`}
              color="secondary"
            />
          ) : (
            <span>
              <span className="bold-label">Requester:</span> {Fmt.DataSubject.fmtFullName(request)}
            </span>
          )}
        </div>
        <div className="text-t2 info-row">
          {!isBatch && (
            <>
              <span className="bold-label">Request ID:</span> {request.id}{" "}
              {request.isGpcOptOut && <>(submitted via GPC)</>}
            </>
          )}
        </div>
        {showDueDate && (
          <div className="request-due-date-section">
            <div className="due-date-container">
              <div className="due-date-text">
                <span className="bold-label">Due:</span>{" "}
                {fmtDateForRow(isBatch ? request.batch.requestEarliestDueDate : request.dueDate)}
              </div>
              {isBatch && (
                <Typography variant="subtitle2" color="textSecondary" className="ml-xs">
                  (Earliest)
                </Typography>
              )}
              {!isBatch && moment().isAfter(moment(request.dueDate), "day") && (
                <span className="past-due-flag">Past Due</span>
              )}
              {!isBatch && canExtend(request) && (
                <button
                  className="extend-due-date-button"
                  onClick={() => {
                    setShowExtendDialog(true);
                  }}
                >
                  Extend Due Date
                </button>
              )}
              {canProcessAsServiceProvider(org, request) && (
                <span className="ml-sm">
                  <AquiloButton
                    color="primary"
                    size="s"
                    onClick={async () => {
                      await Api.dataRequest.setSubjectStatus(org.id, request.id, {
                        subjectStatus: "PROCESSED_AS_SERVICE_PROVIDER",
                      });
                      refreshRequest();
                    }}
                  >
                    Process as Service Provider
                  </AquiloButton>
                </span>
              )}
            </div>
          </div>
        )}
        {request.allowManualAutomation && (
          <div className={"request-date-fields"}>
            <ToolTip
              text={
                request.vendorOutcomes.some((vo) => vo.outcome === "AUTOMATION_FAILED")
                  ? "Some vendors for this request failed to automate. Click to retry."
                  : "Some vendors for this request can now be automatically processed due to updated settings. Click to automate processing."
              }
            >
              <span>
                <LoadingButton
                  variant="contained"
                  className="manually-automate-request-button"
                  loading={isAttemptingAutomation}
                  onClick={handleManuallyAutomateClick}
                >
                  <img src="/assets/images/automation-icon.svg" alt="" />️ Automate Processing
                </LoadingButton>
              </span>
            </ToolTip>
          </div>
        )}

        <ExtendDataRequestDialog
          request={request}
          open={showExtendDialog}
          onClose={() => {
            setShowExtendDialog(false);
          }}
          onDone={() => {
            setShowExtendDialog(false);
          }}
          updateRequestState={extendRequestState}
          handleSubmit={handleExtendSubmit}
        />
      </div>
      {showAutomationConfirm && (
        <div className="tw-boundary">
          <Modal
            title="Submit batch for automation?"
            body={
              "Automating all the requests in a batch will take some time. Requests will be removed from the batch and returned to the inbox if any of their vendors fail automation."
            }
            firstButton="Cancel"
            secondButton="Automate Batch"
            secondButtonColor="primary"
            showOverlay={true}
            onFirstButtonClick={() => setShowAutomationConfirm(false)}
            onSecondButtonClick={handleAutomationConfirm}
            onClose={() => setShowAutomationConfirm(false)}
          />
        </div>
      )}
      {isAttemptingAutomation && (
        <div className="tw-boundary">
          <Modal
            title={`Submitting ${isBatch ? "requests" : "request"} for automation`}
            body={automationErrorBlock}
            showOverlay={true}
            closeOnOverlayClick={false}
            firstButton={
              automationSubmissionMessage || automationSubmissionErrors?.length > 0
                ? "Go to inbox"
                : undefined
            }
            firstButtonColor="tertiary"
            onFirstButtonClick={() => history.push(Routes.requests.pending)}
          />
        </div>
      )}
    </>
  );
};
